
import React, { useEffect, useState } from "react";
import { TLSApi } from "./Tls_ApiService";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Modern color palette
const colors = {
  primary: "#2563eb",
  primaryLight: "#3b82f6",
  secondary: "#64748b",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  background: "#f8fafc",
  surface: "#ffffff",
  text: "#1e293b",
  textLight: "#64748b",
  border: "#e2e8f0",
};

export default function RovingQCReport() {
  // ---------------- STATES ----------------
  const [suId, setSuId] = useState("");
  const [lines, setLines] = useState([]);
  const [items, setItems] = useState([]);
  const [timeSettings, setTimeSettings] = useState([]);
  const [selectedLine, setSelectedLine] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [loading, setLoading] = useState(false);
  const [operatorData, setOperatorData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [color, setColor] = useState("N/A");
  const [styleNo, setStyleNo] = useState("N/A");
  const [hour, setHour] = useState("");
  const [rovingQC, setRovingQC] = useState("");
  const [search, setSearch] = useState("");

  // ---------------- LOAD USER DATA ----------------
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("userData"));
      const id = stored?.data?.su_id;
      setSuId(id);
      setRovingQC(stored?.data?.full_name ?? "");
      if (id) loadLines(id);
      loadTimeSettings();
    } catch (e) {
      // ignore parse errors
      loadTimeSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- API: LOAD LINES ----------------
  const loadLines = async (suIdArg) => {
    try {
      const res = await TLSApi.getTlsLines(suIdArg);
      if (res?.data?.status) {
        // Some APIs return array of objects, keep original behavior if simple array
        setLines(res.data.data.lines || []);
      }
    } catch (err) {
      console.error("loadLines error:", err);
      setLines([]);
    }
  };

  // ---------------- API: LOAD TIME SETTINGS ----------------
  const loadTimeSettings = async () => {
    try {
      const res = await TLSApi.getTlsTimeSettings();
      if (res?.data?.status) {
        setTimeSettings(res.data.data || []);
      } else {
        setTimeSettings([]);
      }
    } catch (err) {
      console.error("loadTimeSettings err:", err);
      setTimeSettings([]);
    }
  };

  // ---------------- LINE CHANGE ----------------
  const handleLineChange = async (lineNo) => {
    setSelectedLine(lineNo);
    setItems([]);
    setSelectedItem("");
    setColor("N/A");
    setStyleNo("N/A");
    setOperatorData([]);
    setFilteredData([]);

    if (!lineNo) return;

    try {
      const res = await TLSApi.getItemsByLineDayReport(lineNo);
      if (res?.data?.status) {
        setItems(res.data.data.items || []);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error("getItemsByLineDayReport err:", err);
      setItems([]);
    }
  };

  // ---------------- ITEM CHANGE ----------------
  const handleItemChange = async (itemNo) => {
    setSelectedItem(itemNo);
    setOperatorData([]);
    setFilteredData([]);
    setColor("N/A");
    setStyleNo("N/A");

    if (!itemNo || !selectedLine) return;

    try {
      const colorRes = await TLSApi.getColorStyle(selectedLine, itemNo);
      if (colorRes?.data?.status && colorRes.data.data?.length) {
        const first = colorRes.data.data[0];
        setColor(first.colour ?? "N/A");
        setStyleNo(first.style_no ?? "N/A");
      }
    } catch (err) {
      console.error("getColorStyle err:", err);
    }

    loadOperatorData(selectedLine, itemNo, selectedDate);
  };

  // ---------------- DATE CHANGE ----------------
  const handleDateChange = (date) => {
    setSelectedDate(date);

    if (selectedLine && selectedItem) {
      loadOperatorData(selectedLine, selectedItem, date);
    }
  };

  // ---------------- LOAD DATA ----------------
  const loadOperatorData = async (line, item, date) => {
    setLoading(true);
    setOperatorData([]);
    setFilteredData([]);

    try {
      const res = await TLSApi.getTlsDayReport(line, item, date);
      if (res?.data?.status) {
        const processed = processOperatorData(res.data);
        setOperatorData(processed);
        setFilteredData(processed);
        // API earlier used res.data.time_id — keep defensive
        setHour(res.data.time_id ?? "");
      } else {
        setOperatorData([]);
        setFilteredData([]);
      }
    } catch (err) {
      console.error("loadOperatorData err:", err);
      setOperatorData([]);
      setFilteredData([]);
    }

    setLoading(false);
  };

  // ---------------- FORMAT DATA ----------------
  const processOperatorData = (data) => {
    const batches = data?.batch_details || [];
    return batches.map((row, index) => {
      let totalDef = 0;
      let pcs = 0;
      let reasons = [];

      const map = {};
      // ensure timeSettings is stable
      (timeSettings || []).forEach((t) => {
        const qc = (row.qc_data || []).find((q) => q.hour === t.time_id);
        const defectsValue = qc ? qc.tot_defects : "";
        map[t.time_id] = { defects: defectsValue };
        if (qc) {
          totalDef += parseInt(qc.tot_defects || 0, 10);
          // original code used pcs += 5 for each qc found
          pcs += 5;
          if (Array.isArray(qc.defects)) {
            qc.defects.forEach((d) => {
              if (d?.reason) reasons.push(d.reason);
            });
          }
        }
      });

      return {
        no: index + 1,
        machine: row.machine_id ?? "",
        operator: row.staff_user?.full_name ?? "",
        operation: row.tls_operation?.tls_op_name ?? "",
        timeDefects: map,
        totalDefects: totalDef,
        totalPcsChecked: pcs,
        percent: pcs ? ((totalDef / pcs) * 100).toFixed(2) + "%" : "0%",
        reason: reasons.join(", "),
      };
    });
  };

  // ---------------- SEARCH FILTER ----------------
  useEffect(() => {
    if (!search.trim()) {
      setFilteredData(operatorData);
      return;
    }

    const q = search.toLowerCase();
    setFilteredData(
      (operatorData || []).filter((d) => {
        return (
          (d.machine || "").toString().toLowerCase().includes(q) ||
          (d.operator || "").toString().toLowerCase().includes(q) ||
          (d.operation || "").toString().toLowerCase().includes(q)
        );
      })
    );
  }, [search, operatorData]);

  // ---------------- DEFECT COLOR LOGIC ----------------
  const defectColor = (value) => {
    if (value === "" || value === null || value === undefined) return colors.text;
    const numValue = parseInt(value, 10);
    if (Number.isNaN(numValue)) return colors.text;
    if (numValue === 0) return colors.success;
    if (numValue === 1) return colors.danger;
    if (numValue > 1) return colors.warning;
    return colors.text;
  };

  // ---------------- EXPORT EXCEL ----------------
  const exportExcel = () => {
    try {
      // create a flattened array for excel (time columns expanded)
      const headers = [
        "S.No",
        "Machine",
        "Operator",
        "Operation",
        ...timeSettings.map((t) => t.time_id),
        "Total Def",
        "Checked",
        "Percent",
        "Reason",
      ];

      const rows = (filteredData || []).map((d) => {
        const row = {
          "S.No": d.no,
          Machine: d.machine,
          Operator: d.operator,
          Operation: d.operation,
        };
        (timeSettings || []).forEach((t) => {
          row[t.time_id] =
            d.timeDefects && d.timeDefects[t.time_id]
              ? d.timeDefects[t.time_id].defects
              : "";
        });
        row["Total Def"] = d.totalDefects;
        row["Checked"] = d.totalPcsChecked;
        row["Percent"] = d.percent;
        row["Reason"] = d.reason;
        return row;
      });

      const sheet = XLSX.utils.json_to_sheet(rows, { header: headers });
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "RovingQC");
      XLSX.writeFile(book, `RovingQC_${selectedDate}.xlsx`);
    } catch (err) {
      console.error("exportExcel err:", err);
    }
  };

  // ---------------- EXPORT PDF ----------------
  const exportPDF = () => {
    try {
      const pdf = new jsPDF("landscape");
      const head = [
        [
          "S.No",
          "Machine",
          "Operator",
          "Operation",
          ...timeSettings.map((t) => t.time_id),
          "Total Def",
          "Checked",
          "%",
          "Reason",
        ],
      ];

      const body = (filteredData || []).map((d) => {
        const base = [d.no, d.machine, d.operator, d.operation];
        const times = (timeSettings || []).map((t) => {
          return d.timeDefects && d.timeDefects[t.time_id]
            ? d.timeDefects[t.time_id].defects
            : "";
        });
        return [
          ...base,
          ...times,
          d.totalDefects,
          d.totalPcsChecked,
          d.percent,
          d.reason,
        ];
      });

      autoTable(pdf, {
        head,
        body,
        startY: 10,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [37, 99, 235] }, // primary color approximate
        alternateRowStyles: { fillColor: [245, 247, 250] },
      });
      pdf.save(`RovingQC_${selectedDate}.pdf`);
    } catch (err) {
      console.error("exportPDF err:", err);
    }
  };

  // ---------------- CUSTOM STYLES (JS OBJECTS) ----------------
  const containerStyle = {
    background: colors.background,
    minHeight: "100vh",
    padding: "24px",
  };

  const cardStyle = {
    background: colors.surface,
    borderRadius: "12px",
    padding: "24px",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    border: `1px solid ${colors.border}`,
  };

  const headerStyle = {
    color: colors.text,
    fontSize: "28px",
    fontWeight: "700",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  };

  const subHeaderStyle = {
    color: colors.textLight,
    fontSize: "14px",
    marginBottom: "24px",
  };

  const infoCardStyle = {
    background: `linear-gradient(135deg, ${colors.primary}15, ${colors.primary}08)`,
    border: `1px solid ${colors.primary}20`,
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "24px",
  };

  const buttonBaseStyle = {
    background: colors.primary,
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    transition: "all 0.15s ease",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  };

  const buttonHoverStyle = {
    background: colors.primaryLight,
    transform: "translateY(-1px)",
    boxShadow: "0 6px 14px rgba(37, 99, 235, 0.18)",
  };

  // simple hover handlers for buttons (mutating style is acceptable for hover)
  const onBtnMouseEnter = (e) =>
    Object.assign(e.currentTarget.style, {
      background: buttonHoverStyle.background,
      transform: buttonHoverStyle.transform,
      boxShadow: buttonHoverStyle.boxShadow,
    });
  const onBtnMouseLeave = (e) =>
    Object.assign(e.currentTarget.style, {
      background: buttonBaseStyle.background,
      transform: "none",
      boxShadow: "none",
    });

  return (
    <div style={containerStyle}>
      {/* Embedded CSS for stable focus/size behavior */}
      <style>{`
        .tls-input, .tls-select {
          width: 100%;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid ${colors.border};
          background: ${colors.surface};
          color: ${colors.text};
          font-size: 14px;
          outline: none;
          transition: all 0.16s ease;
          box-sizing: border-box;
        }

        .tls-input:focus, .tls-select:focus {
          border-color: ${colors.primary};
          box-shadow: 0 0 0 3px ${colors.primary}20;
        }

        .tls-select[disabled], .tls-input[disabled] {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .tls-button {
          background: ${colors.primary};
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.15s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .tls-button:active {
          transform: translateY(1px);
        }

        /* Ensure DataTable container doesn't collapse */
        .roving-table-wrapper {
          width: 100%;
          overflow: auto;
        }
      `}</style>

      <div style={cardStyle}>
        {/* Header Section */}
        <div style={{ marginBottom: "32px" }}>
          <h1 style={headerStyle}>
            <div
              style={{
                width: "4px",
                height: "28px",
                background: colors.primary,
                borderRadius: "2px",
              }}
            ></div>
            Roving QC Report
          </h1>

        </div>

        {/* Filters Section */}
        <div style={{ marginBottom: "24px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            {/* Line Selector */}
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: colors.text,
                  fontSize: "14px",
                }}
              >
                Production Line
              </label>
              <select
                className="tls-select"
                value={selectedLine}
                onChange={(e) => handleLineChange(e.target.value)}
              >
                <option value="">Select Line</option>
                {Array.isArray(lines) &&
                  lines.map((l, idx) => (
                    <option key={l?.id ?? l ?? idx} value={l?.line_no ?? l}>
                      {l?.line_no ?? l}
                    </option>
                  ))}
              </select>
            </div>

            {/* Item Selector */}
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: colors.text,
                  fontSize: "14px",
                }}
              >
                Item Number
              </label>
              <select
                className="tls-select"
                value={selectedItem}
                onChange={(e) => handleItemChange(e.target.value)}
                disabled={!selectedLine}
              >
                <option value="">Select Item</option>
                {Array.isArray(items) &&
                  items.map((i, idx) => (
                    <option key={i?.item_no ?? idx} value={i?.item_no ?? ""}>
                      {i?.item_no ?? ""} {i?.batch_status ? `(${i.batch_status})` : ""}
                    </option>
                  ))}
              </select>
            </div>

            {/* Date Selector */}
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: colors.text,
                  fontSize: "14px",
                }}
              >
                Date
              </label>
              <input
                type="date"
                className="tls-input"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
              />
            </div>
          </div>

          {/* Search Box */}
          <div style={{ maxWidth: "360px" }}>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                marginBottom: "8px",
                color: colors.text,
                fontSize: "14px",
              }}
            >
              Search
            </label>
            <input
              type="text"
              placeholder="Search by machine, operator, or operation..."
              className="tls-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Information Cards */}
        <div style={infoCardStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: colors.textLight,
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              >
                COLOR
              </div>
              <div
                style={{ fontSize: "16px", color: colors.text, fontWeight: "600" }}
              >
                {color}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: colors.textLight,
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              >
                STYLE NUMBER
              </div>
              <div
                style={{ fontSize: "16px", color: colors.text, fontWeight: "600" }}
              >
                {styleNo}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: colors.textLight,
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              >
                HOUR
              </div>
              <div
                style={{ fontSize: "16px", color: colors.text, fontWeight: "600" }}
              >
                {hour}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: colors.textLight,
                  fontWeight: "600",
                  marginBottom: "4px",
                }}
              >
                QC USER
              </div>
              <div
                style={{ fontSize: "16px", color: colors.text, fontWeight: "600" }}
              >
                {rovingQC}
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div
          className="roving-table-wrapper"
          style={{
            borderRadius: "8px",
            overflow: "hidden",
            border: `1px solid ${colors.border}`,
            marginBottom: "24px",
          }}
        >
          <DataTable
            columns={[
              {
                name: "S.No",
                selector: (row) => row.no,
                width: "80px",
                sortable: true,
              },
              {
                name: "Machine",
                selector: (row) => row.machine,
                sortable: true,
              },
              {
                name: "Operator",
                selector: (row) => row.operator,
                 minWidth: "200px",    
  grow: 2,
                sortable: true,
              },
              {
                name: "Operation",
                selector: (row) => row.operation,
                minWidth: "260px",    // increase space
  grow: 3,
                sortable: true,
                wrap: true,
              },

              // Hour Columns with Color (guarded)
              ...((timeSettings || []).map((t) => ({
                name: t.time_id,
                selector: (row) =>
                  row?.timeDefects && row.timeDefects[t.time_id]
                    ? row.timeDefects[t.time_id].defects
                    : "",
                cell: (row) => {
                  const val =
                    row?.timeDefects && row.timeDefects[t.time_id]
                      ? row.timeDefects[t.time_id].defects
                      : "";
                  return (
                    <span
                      style={{
                        color: defectColor(val),
                        fontWeight: "700",
                        fontSize: "14px",
                      }}
                    >
                      {val}
                    </span>
                  );
                },
                center: true,
              }))),

              // Total Defects
              {
                name: "Total Defects",
                selector: (row) => row.totalDefects,
                cell: (row) => (
                  <span
                    style={{
                      color: colors.text,
                      fontWeight: "700",
                      background:
                        row.totalDefects > 0 ? `${colors.danger}15` : `${colors.success}15`,
                      padding: "4px 12px",
                      borderRadius: "20px",
                      fontSize: "14px",
                    }}
                  >
                    {row.totalDefects}
                  </span>
                ),
                minWidth: "200px",    // increase space
  grow: 2,
                center: true,
                sortable: true,
              },

              {
                name: "Checked",
                selector: (row) => row.totalPcsChecked,
                center: true,
                sortable: true,
              },
              {
                name: "Defect %",
                selector: (row) => row.percent,
                center: true,
                sortable: true,
              },
              {
                name: "Reason",
                selector: (row) => row.reason,
                wrap: true,
                grow: 2,
              },
            ]}
            data={filteredData || []}
            pagination
            highlightOnHover
            dense
            progressPending={loading}
            progressComponent={
              <div style={{ padding: "20px", textAlign: "center", color: colors.textLight }}>
                Loading data...
              </div>
            }
            customStyles={{
              headCells: {
                style: {
                  background: colors.background,
                  color: colors.text,
                  fontWeight: "700",
                  fontSize: "14px",
                  padding: "16px 12px",
                },
              },
              cells: {
                style: {
                  padding: "12px",
                  fontSize: "14px",
                  color: colors.text,
                },
              },
              rows: {
                style: {
                  borderBottom: `1px solid ${colors.border}`,
                  "&:hover": {
                    background: `${colors.primary}08`,
                  },
                },
              },
            }}
          />
        </div>

        {/* Export Buttons */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            className="tls-button"
            style={buttonBaseStyle}
            onMouseEnter={onBtnMouseEnter}
            onMouseLeave={onBtnMouseLeave}
            onClick={exportExcel}
          >
            Export Excel
          </button>
          <button
            className="tls-button"
            style={buttonBaseStyle}
            onMouseEnter={onBtnMouseEnter}
            onMouseLeave={onBtnMouseLeave}
            onClick={exportPDF}
          >
            Export PDF
          </button>
        </div>
      </div>
    </div>
  );
}
